import {useEffect, useState} from 'react'

import {sideNavSizes, topNavSizes} from '../UI/navigation/styles.js'

// Where the navigation of the planner, customer and admin layouts is: the bar at the top or the rail at the left.
// Chosen per browser (localStorage), changing it re-renders the layouts that subscribe, no reload needed.

export type NavPosition = 'top' | 'side'

// The item in the account menu that switches between the two is hidden for now: everyone gets the top bar, the side
// rail only shows when localStorage._navPosition is 'side' (set it by hand to try it)
export const showNavPositionSwitch = false

const storageKey = '_navPosition'

const listeners = new Set<() => void>()

let current: NavPosition | undefined

function read(): NavPosition {
    try {
        return localStorage.getItem(storageKey) === 'side' ? 'side' : 'top'
    } catch {
        // No storage (private mode, blocked site data, tests)
        return 'top'
    }
}

export function getNavPosition(): NavPosition {
    current = current || read()
    return current
}

export function setNavPosition(position: NavPosition) {
    current = position

    try {
        localStorage.setItem(storageKey, position)
    } catch {
        // Still switches for this page
    }

    listeners.forEach((listener) => listener())
}

/** Calls listener when the position changes, returns the unsubscribe function */
export function subscribeNavPosition(listener: () => void) {
    listeners.add(listener)
    return () => {
        listeners.delete(listener)
    }
}

export function useNavPosition(): NavPosition {
    const [position, setPosition] = useState(getNavPosition)

    useEffect(() => subscribeNavPosition(() => setPosition(getNavPosition())), [])

    return position
}

/**
 * The space the navigation takes from the page: the height of the top bar or the width of the side rail. For pages
 * that size themselves to the viewport, e.g. height: calc(100vh - top - 60px).
 */
export function navOffset(position: NavPosition = getNavPosition()) {
    return position === 'side' ? {top: 0, left: sideNavSizes.width} : {top: topNavSizes.height, left: 0}
}
